import styled from '@emotion/styled'
import { Select, Spin } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import type { OrgCompetitionListItem } from '@sportninja/common/services/competition'
import { useOrgCompetitions } from '../hooks/useOrgCompetitions'

// Show the blinking text caret while searching, but hide it once a value is
// selected. The parent wizard form hides it unconditionally
// (caret-color: transparent), which makes the empty field look non-typeable.
// antd renders `.ant-select-selection-item` only when a value is chosen, so
// `:not(:has(...))` re-enables the caret only in the unselected state — plus
// an I-beam mouse cursor to signal the field accepts text. `!important` +
// later source order beats the parent's `!important` rule.
const SearchableSelect = styled(Select)`
  &.ant-select-show-search:not(.ant-select-disabled):not(
      :has(.ant-select-selection-item)
    )
    .ant-select-selection-search,
  &.ant-select-show-search:not(.ant-select-disabled):not(
      :has(.ant-select-selection-item)
    )
    .ant-select-selection-search-input {
    caret-color: auto !important;
  }
  &:not(.ant-select-disabled) .ant-select-selector {
    cursor: text !important;
  }
`

type Props = {
  orgId: string
  value: string | null
  onChange: (item: OrgCompetitionListItem | null) => void
  /** Restrict results to a specific sport (used on Step 3 with Step 2's sport selection). */
  sportId?: string | null
  /** Restrict results to a specific generic sport (used on Step 3 with Step 2's selection). */
  genericSportId?: string | null
  /** Override the default 'Select a competition to clone' placeholder. */
  placeholder?: string
}

const useDebounced = (value: string, delay = 300) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const formatDateRange = (
  startsAt: string | null,
  endsAt: string | null
): string | null => {
  if (!startsAt && !endsAt) {
    return null
  }
  const fmt = (iso: string | null) => (iso ? dayjs(iso).format('MMM D') : '—')
  return `${fmt(startsAt)} - ${fmt(endsAt)}`
}

const SourceCompetitionPicker = ({
  orgId,
  value,
  onChange,
  sportId,
  genericSportId,
  placeholder = 'Select a competition to clone',
}: Props) => {
  const [search, setSearch] = useState('')
  const debounced = useDebounced(search, 300)
  const { data, isFetching } = useOrgCompetitions(orgId, debounced, {
    sportId,
    genericSportId,
  })
  const items = data ?? []

  const renderRow = (item: OrgCompetitionListItem) => {
    const dateRange = formatDateRange(item.starts_at, item.ends_at)
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          lineHeight: 1.2,
        }}
      >
        <span style={{ color: colors.WHITE, fontSize: 14 }}>
          {item.name_full}
        </span>
        {dateRange ? (
          <span style={{ color: colors.NEUTRAL_50, fontSize: 12 }}>
            {dateRange}
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <SearchableSelect
      showSearch
      allowClear
      placeholder={placeholder}
      value={value ?? undefined}
      onChange={(v) => {
        const id = (v as string | undefined) ?? null
        const item = id ? items.find((c) => c.id === id) ?? null : null
        onChange(item)
      }}
      onSearch={setSearch}
      filterOption={false}
      notFoundContent={
        isFetching ? <Spin size='small' /> : 'No competitions found'
      }
      options={items.map((c) => ({
        value: c.id,
        // `label` is used for the search input's text/aria; keep it as the
        // plain name. Visuals come from optionRender / labelRender below.
        label: c.name_full,
      }))}
      optionRender={(option) => {
        const item = items.find((c) => c.id === option.value)
        return item ? renderRow(item) : option.label
      }}
      labelRender={(props) => {
        const item = items.find((c) => c.id === props.value)
        return item ? renderRow(item) : props.label
      }}
      style={{ width: '100%' }}
    />
  )
}

export default SourceCompetitionPicker
